/**
 * Time on a case — FR-E11, T-2.11. Split out of `CasePanels.tsx` in Wave 15C
 * and drawn as the kit's table; the behaviour is unchanged.
 *
 * The rate column appears only when the firm prices by the hour. `null` is not
 * zero — it is "this firm does not price by the hour" — so nothing is rendered
 * rather than a rupee sign with no number.
 */

import { Alert, Button, Input, formatDate } from '@fiveox/ui'
import { Clock } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { type ListColumn, ListTable, RecordSection, useListTable } from '../../components/page'
import { type TimeEntry, caseFlows, hoursLabel } from '../../lib/api/cases'
import { useAction } from './useAction'

export function CaseTime({ caseId, canUpdate }: { caseId: string; canUpdate: boolean }) {
  const [rows, setRows] = useState<TimeEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [minutes, setMinutes] = useState('')
  const [note, setNote] = useState('')
  const { error, busy, run } = useAction()

  const load = useCallback(async () => {
    const body = await caseFlows.time(caseId)
    setRows(body.data)
    setTotal(Number(body.meta?.case_minutes ?? 0))
  }, [caseId])

  useEffect(() => {
    void load()
      .catch(() => undefined)
      .finally(() => setLoading(false))
  }, [load])

  const priced = rows.some((row) => row.rate !== null)
  const columns = useMemo<ListColumn<TimeEntry>[]>(
    () => [
      {
        key: 'spent_on',
        label: 'Day',
        width: 110,
        sortField: 'spent_on',
        render: (row) => <span className="whitespace-nowrap">{formatDate(row.spent_on)}</span>,
      },
      {
        key: 'minutes',
        label: 'Time',
        width: 90,
        sortField: 'minutes',
        render: (row) => <span className="tabular-nums">{hoursLabel(row.minutes)}</span>,
      },
      {
        key: 'note',
        label: 'What',
        width: 200,
        render: (row) => <span className="text-muted-foreground">{row.note ?? '—'}</span>,
      },
      ...(priced
        ? [
            {
              key: 'rate',
              label: 'Rate',
              width: 90,
              align: 'right' as const,
              render: (row: TimeEntry) =>
                row.rate !== null ? (
                  <span className="text-muted-foreground tabular-nums">₹{row.rate}/hr</span>
                ) : null,
            },
          ]
        : []),
    ],
    [priced],
  )
  const list = useListTable(rows, { columns, initialSort: { field: 'spent_on', dir: 'desc' } })

  return (
    <RecordSection
      id="case-time"
      icon={<Clock size={18} aria-hidden />}
      title="Time"
      description={`${hoursLabel(total)} on this case.`}
    >
      <div className="space-y-4">
        {error && <Alert tone="error">{error}</Alert>}
        <ListTable
          list={list}
          rowKey={(row) => row.id}
          loading={loading}
          empty={<span className="text-muted-foreground text-sm">No time logged yet.</span>}
        />

        {canUpdate && (
          <form
            className="border-border grid gap-3 border-t pt-4 sm:grid-cols-[8rem_minmax(0,1fr)_auto] sm:items-end"
            onSubmit={(event) => {
              event.preventDefault()
              if (!minutes) return
              void run(async () => {
                await caseFlows.logTime(caseId, {
                  minutes: Number(minutes),
                  note: note.trim() || undefined,
                })
                setMinutes('')
                setNote('')
                await load()
              }, 'The time could not be logged.')
            }}
          >
            <Input
              size="sm"
              label="Minutes"
              type="number"
              min={1}
              max={1440}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
            <Input
              size="sm"
              label="What were you doing?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <Button type="submit" size="sm" disabled={!minutes || busy}>
              Log time
            </Button>
          </form>
        )}
      </div>
    </RecordSection>
  )
}
